import { Fragment, useCallback, useEffect, useState } from 'react';
import { ArrowDownTrayIcon, ArrowPathIcon, KeyIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import { PageBackButton } from '@/components/common/PageBackButton';
import {
  ssoLogService,
  type SsoDeviceForm,
  type SsoFailureReason,
  type SsoLogClient,
  type SsoLogFlow,
  type SsoLogListResult,
  type SsoLogSortBy,
  type SsoLogSortDir,
  type SsoLogStatus,
  type SsoLogStatusFilter,
  type SsoLogStep,
} from '@/services/sysadmin/ssoLogService';
import { buildSsoLogCsv, downloadSsoLogCsv, ssoLogCsvFilename } from './ssoLogCsv';

const DAYS_OPTIONS = [1, 7, 14, 30, 90];

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat('sv-SE', { dateStyle: 'short', timeStyle: 'short' }).format(d);
}

function userLabel(flow: SsoLogFlow): string | null {
  const name = flow.userName && flow.userName !== flow.email ? flow.userName.trim() : '';
  const rsid = flow.userRsid?.trim() ? `RSID ${flow.userRsid.trim()}` : '';
  const label = [name, rsid].filter(Boolean).join(' · ');
  return label || null;
}

function loginAddress(flow: SsoLogFlow, t: (key: string, opts?: Record<string, string>) => string): {
  primary: string;
  sentLine: string | null;
  note: string | null;
} {
  const known = flow.email?.trim() || '';
  const sent = flow.sentLoginHint?.trim() || '';
  const isSkane = /skåne|skane/i.test(`${flow.customerName ?? ''} ${flow.organizationName ?? ''}`);
  const note = sent
    ? null
    : isSkane
      ? t('sysAdmin.ssoLog.idpUsernameFilledThere')
      : t('sysAdmin.ssoLog.nothingPrefillsIdp');
  if (known) {
    return {
      primary: known,
      sentLine: sent
        ? t('sysAdmin.ssoLog.sentToIdp', { value: sent })
        : t('sysAdmin.ssoLog.sentToIdpNone'),
      note,
    };
  }
  return {
    primary: t('sysAdmin.ssoLog.nothingSentToIdp'),
    sentLine: null,
    note,
  };
}

function customerLabel(flow: SsoLogFlow): string {
  return flow.customerName || flow.organizationName || '–';
}

function LoginAddressCell({
  flow,
  statusText,
  t,
}: {
  flow: SsoLogFlow;
  statusText: string;
  t: (key: string, opts?: Record<string, string>) => string;
}) {
  const address = loginAddress(flow, t);
  return (
    <>
      <div>{address.primary}</div>
      {address.sentLine && (
        <div className="text-xs text-textTertiary">{address.sentLine}</div>
      )}
      {address.note && (
        <div className="text-xs text-textTertiary">{address.note}</div>
      )}
      {flow.status !== 'success' && (
        <div className="text-xs text-textTertiary">{statusText}</div>
      )}
    </>
  );
}

function SortTh({
  label,
  column,
  sortBy,
  sortDir,
  onSort,
}: {
  label: string;
  column: SsoLogSortBy;
  sortBy: SsoLogSortBy;
  sortDir: SsoLogSortDir;
  onSort: (column: SsoLogSortBy) => void;
}) {
  const active = sortBy === column;
  return (
    <th className="text-left px-3 py-2">
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 font-medium hover:text-textDefault"
      >
        {label}
        <span className="text-xs">{active ? (sortDir === 'asc' ? '↑' : '↓') : ''}</span>
      </button>
    </th>
  );
}

export default function SsoLogPage() {
  const { t } = useTranslation();
  const [days, setDays] = useState(7);
  const [organizationId, setOrganizationId] = useState<number | ''>('');
  const [email, setEmail] = useState('');
  const [client, setClient] = useState<SsoLogClient | ''>('');
  const [device, setDevice] = useState<SsoDeviceForm | ''>('');
  const [status, setStatus] = useState<SsoLogStatusFilter>('unsuccessful');
  const [sortBy, setSortBy] = useState<SsoLogSortBy>('lastAt');
  const [sortDir, setSortDir] = useState<SsoLogSortDir>('desc');
  const [data, setData] = useState<SsoLogListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [openFlowId, setOpenFlowId] = useState<string | null>(null);
  const [steps, setSteps] = useState<SsoLogStep[] | null>(null);
  const [exporting, setExporting] = useState(false);
  const pageSize = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await ssoLogService.list({
        days,
        organizationId: organizationId || undefined,
        email: email.trim() || undefined,
        client,
        device,
        status,
        sortBy,
        sortDir,
        limit: pageSize,
        offset,
      });
      setData(result);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || t('sysAdmin.ssoLog.loadError'));
    } finally {
      setLoading(false);
    }
  }, [client, days, device, email, offset, organizationId, sortBy, sortDir, status, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const reasonText = (reason: SsoFailureReason) => t(`sysAdmin.ssoLog.reason.${reason}`);
  const statusText = (value: SsoLogStatus) => t(`sysAdmin.ssoLog.status.${value}`);
  const deviceText = (flow: SsoLogFlow) => {
    const form = t(`sysAdmin.ssoLog.device.${flow.deviceForm}`);
    if (flow.deviceOs === 'unknown') return form;
    return `${form} · ${t(`sysAdmin.ssoLog.os.${flow.deviceOs}`)}`;
  };

  const openSteps = async (flow: SsoLogFlow) => {
    if (!flow.flowId) {
      setOpenFlowId(null);
      setSteps(null);
      return;
    }
    if (openFlowId === flow.flowId) {
      setOpenFlowId(null);
      setSteps(null);
      return;
    }
    try {
      const next = await ssoLogService.getSteps(flow.flowId);
      setOpenFlowId(flow.flowId);
      setSteps(next);
    } catch {
      toast.error(t('sysAdmin.ssoLog.loadError'));
    }
  };

  const applyFilters = () => {
    setOffset(0);
    void load();
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const result = await ssoLogService.list({
        days,
        organizationId: organizationId || undefined,
        email: email.trim() || undefined,
        client,
        device,
        status,
        sortBy,
        sortDir,
        limit: 2000,
        offset: 0,
      });
      const headers = [
        t('sysAdmin.ssoLog.colCustomer'),
        t('sysAdmin.ssoLog.colWhen'),
        t('sysAdmin.ssoLog.colEmail'),
        t('sysAdmin.ssoLog.colSentToIdp'),
        t('sysAdmin.ssoLog.colUser'),
        t('sysAdmin.ssoLog.colChannel'),
        t('sysAdmin.ssoLog.colDevice'),
        t('sysAdmin.ssoLog.colReason'),
        t('sysAdmin.ssoLog.statusLabel'),
      ];
      const rows = result.items.map((flow) => {
        const address = loginAddress(flow, t);
        return [
          customerLabel(flow),
          formatWhen(flow.lastAt),
          address.primary,
          flow.sentLoginHint || t('sysAdmin.ssoLog.sentToIdpNone'),
          userLabel(flow) || '',
          t(`sysAdmin.ssoLog.client.${flow.client}`),
          deviceText(flow),
          [reasonText(flow.reason), flow.errorMessage].filter(Boolean).join(' — '),
          statusText(flow.status),
        ];
      });
      downloadSsoLogCsv(ssoLogCsvFilename(), buildSsoLogCsv(headers, rows));
    } catch {
      toast.error(t('sysAdmin.ssoLog.exportError'));
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (column: SsoLogSortBy) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir(column === 'customer' ? 'asc' : 'desc');
    }
    setOffset(0);
  };

  const summary = data?.summary;
  const from = data && data.total > 0 ? offset + 1 : 0;
  const to = data ? Math.min(offset + data.items.length, data.total) : 0;

  return (
    <div className="p-6 space-y-6 text-textDefault">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <PageBackButton />
          <KeyIcon className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">{t('sysAdmin.ssoLog.title')}</h1>
            <p className="text-sm text-textSecondary">{t('sysAdmin.ssoLog.description')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void exportCsv()}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded hover:bg-bgLight disabled:opacity-40"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {t('common.exportCsv')}
          </button>
          <button
            type="button"
            onClick={() => void load()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded hover:bg-bgLight"
          >
            <ArrowPathIcon className="w-4 h-4" />
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(['failed', 'abandoned', 'in_progress', 'success'] as SsoLogStatus[]).map((key) => (
            <div key={key} className="border border-border rounded-lg p-3 bg-bgElevated">
              <div className="text-xs text-textSecondary">{statusText(key)}</div>
              <div className="text-2xl font-semibold">{summary[key]}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-end border border-border rounded-lg p-4 bg-bgElevated">
        <label className="text-sm">
          <span className="block text-textSecondary mb-1">{t('sysAdmin.ssoLog.period')}</span>
          <select
            value={days}
            onChange={(e) => { setDays(Number(e.target.value)); setOffset(0); }}
            className="border border-border rounded px-2 py-1.5 bg-bgContainer"
          >
            {DAYS_OPTIONS.map((d) => (
              <option key={d} value={d}>{t('sysAdmin.ssoLog.days', { count: d })}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-textSecondary mb-1">{t('sysAdmin.ssoLog.customer')}</span>
          <select
            value={organizationId}
            onChange={(e) => { setOrganizationId(e.target.value ? Number(e.target.value) : ''); setOffset(0); }}
            className="border border-border rounded px-2 py-1.5 bg-bgContainer min-w-[12rem]"
          >
            <option value="">{t('sysAdmin.ssoLog.allOrgs')}</option>
            {(data?.organizations ?? []).map((org) => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-textSecondary mb-1">{t('sysAdmin.ssoLog.user')}</span>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
            placeholder={t('sysAdmin.ssoLog.userPlaceholder')}
            className="border border-border rounded px-2 py-1.5 bg-bgContainer min-w-[14rem]"
          />
        </label>
        <label className="text-sm">
          <span className="block text-textSecondary mb-1">{t('sysAdmin.ssoLog.channel')}</span>
          <select
            value={client}
            onChange={(e) => { setClient(e.target.value as SsoLogClient | ''); setOffset(0); }}
            className="border border-border rounded px-2 py-1.5 bg-bgContainer"
          >
            <option value="">{t('common.all')}</option>
            <option value="web">{t('sysAdmin.ssoLog.client.web')}</option>
            <option value="mobile">{t('sysAdmin.ssoLog.client.mobile')}</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-textSecondary mb-1">{t('sysAdmin.ssoLog.deviceLabel')}</span>
          <select
            value={device}
            onChange={(e) => { setDevice(e.target.value as SsoDeviceForm | ''); setOffset(0); }}
            className="border border-border rounded px-2 py-1.5 bg-bgContainer"
          >
            <option value="">{t('common.all')}</option>
            <option value="phone">{t('sysAdmin.ssoLog.device.phone')}</option>
            <option value="tablet">{t('sysAdmin.ssoLog.device.tablet')}</option>
            <option value="computer">{t('sysAdmin.ssoLog.device.computer')}</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-textSecondary mb-1">{t('sysAdmin.ssoLog.statusLabel')}</span>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value as SsoLogStatusFilter); setOffset(0); }}
            className="border border-border rounded px-2 py-1.5 bg-bgContainer"
          >
            <option value="unsuccessful">{t('sysAdmin.ssoLog.status.unsuccessful')}</option>
            <option value="failed">{t('sysAdmin.ssoLog.status.failed')}</option>
            <option value="abandoned">{t('sysAdmin.ssoLog.status.abandoned')}</option>
            <option value="in_progress">{t('sysAdmin.ssoLog.status.in_progress')}</option>
            <option value="success">{t('sysAdmin.ssoLog.status.success')}</option>
          </select>
        </label>
      </div>

      <div className="overflow-x-auto border border-border rounded-lg bg-bgElevated">
        <table className="min-w-full text-sm">
          <thead className="bg-bgLight text-textSecondary">
            <tr>
              <SortTh label={t('sysAdmin.ssoLog.colCustomer')} column="customer" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortTh label={t('sysAdmin.ssoLog.colWhen')} column="lastAt" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.ssoLog.colEmail')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.ssoLog.colUser')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.ssoLog.colChannel')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.ssoLog.colDevice')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.ssoLog.colReason')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-textSecondary">{t('common.loading')}</td></tr>
            )}
            {!loading && data?.items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-textSecondary">{t('sysAdmin.ssoLog.empty')}</td></tr>
            )}
            {!loading && data?.items.map((flow) => (
              <Fragment key={flow.flowId ?? `${flow.lastAt}-${flow.email ?? ''}`}>
                <tr
                  className="border-t border-border hover:bg-bgLight cursor-pointer"
                  onClick={() => void openSteps(flow)}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium">{customerLabel(flow)}</div>
                    {flow.organizationName && flow.customerName && flow.organizationName !== flow.customerName && (
                      <div className="text-xs text-textTertiary">{flow.organizationName}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{formatWhen(flow.lastAt)}</td>
                  <td className="px-3 py-2">
                    <LoginAddressCell flow={flow} statusText={statusText(flow.status)} t={t} />
                  </td>
                  <td className="px-3 py-2">{userLabel(flow) || '–'}</td>
                  <td className="px-3 py-2">{t(`sysAdmin.ssoLog.client.${flow.client}`)}</td>
                  <td className="px-3 py-2">{deviceText(flow)}</td>
                  <td className="px-3 py-2">
                    <div>{reasonText(flow.reason)}</div>
                    {flow.errorMessage && (
                      <div className="text-xs text-red-700 dark:text-red-300 mt-0.5">{flow.errorMessage}</div>
                    )}
                  </td>
                </tr>
                {openFlowId && flow.flowId === openFlowId && steps && (
                  <tr className="border-t border-border bg-bgContainer">
                    <td colSpan={7} className="px-3 py-3">
                      <ol className="space-y-1 text-xs">
                        {steps.map((step) => (
                          <li key={step.id}>
                            <span className="font-mono">{formatWhen(step.createdAt)}</span>
                            {' · '}
                            <span className={step.success ? '' : 'text-red-700 dark:text-red-300'}>{step.step}</span>
                            {step.email ? ` · ${step.email}` : ''}
                            {step.errorMessage ? ` · ${step.errorMessage}` : ''}
                          </li>
                        ))}
                      </ol>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > 0 && (
        <div className="flex items-center justify-between text-sm text-textSecondary">
          <span>{t('sysAdmin.ssoLog.showing', { from, to, total: data.total })}</span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - pageSize))}
              className="px-3 py-1 border border-border rounded disabled:opacity-40"
            >
              {t('common.previous')}
            </button>
            <button
              type="button"
              disabled={offset + pageSize >= data.total}
              onClick={() => setOffset(offset + pageSize)}
              className="px-3 py-1 border border-border rounded disabled:opacity-40"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
